import React, { useState, useEffect, useCallback, useMemo, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '../libs/appContextLib';

/**
 * Navigation Context
 *
 * Tracks current navigation context for intelligent suggestions.
 * Provides state about:
 * - Current route
 * - Active recipe (if on Recipe Designer)
 * - Selected tools in recipe
 * - Recent navigation history
 * - Last execution time
 */

// Create context for sharing navigation state across components
const NavigationContext = createContext(null);

/**
 * Hook to access navigation context
 * Must be used within NavigationContextProvider
 */
export const useNavigationContext = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigationContext must be used within NavigationContextProvider');
  }
  return context;
};

/**
 * Navigation Context Provider
 * Wrap your app with this to enable context tracking
 */
export const NavigationContextProvider = ({ children }) => {
  const location = useLocation();
  const { currUser } = useAppContext();

  // Current route (path without trailing slash)
  const [currentRoute, setCurrentRoute] = useState('');

  // Active recipe being edited (set by Recipe Designer)
  const [activeRecipe, setActiveRecipe] = useState(null);

  // Selected tools in current recipe
  const [selectedTools, setSelectedTools] = useState([]);

  // Recent routes navigation history (last 10)
  const [recentRoutes, setRecentRoutes] = useState([]);

  // Last recipe execution timestamp
  const [lastExecutionTime, setLastExecutionTime] = useState(null);

  // Current page context (for suggestions)
  const [pageContext, setPageContext] = useState({});

  // Update current route when location changes
  useEffect(() => {
    const path = location.pathname;
    const routeId = path.split('/')[1] || '';
    setCurrentRoute(routeId);

    // Track recent routes (exclude recipe-specific paths)
    if (routeId && !routeId.startsWith('recipe-')) {
      setRecentRoutes(prev => {
        const filtered = prev.filter(r => r !== routeId);
        const updated = [routeId, ...filtered].slice(0, 10);
        return updated;
      });
    }
  }, [location.pathname]);

  // Update selected tools when recipe changes
  useEffect(() => {
    if (activeRecipe?.steps) {
      const tools = activeRecipe.steps.map(s => s.toolId).filter(Boolean);
      setSelectedTools(tools);
    } else {
      setSelectedTools([]);
    }
  }, [activeRecipe]);

  // Callback to update active recipe (called by Recipe Designer)
  const updateActiveRecipe = useCallback((recipe) => {
    setActiveRecipe(recipe);
  }, []);

  // Callback to mark recipe execution
  const markExecution = useCallback(() => {
    setLastExecutionTime(Date.now());
  }, []);

  // Callback to update page context
  const updatePageContext = useCallback((context) => {
    setPageContext(prev => ({
      ...prev,
      ...context,
    }));
  }, []);

  // Clear context (useful when navigating away)
  const clearContext = useCallback(() => {
    setActiveRecipe(null);
    setSelectedTools([]);
    setPageContext({});
  }, []);

  const contextValue = {
    // State
    currentRoute,
    activeRecipe,
    selectedTools,
    recentRoutes,
    lastExecutionTime,
    pageContext,
    userRole: currUser?.role,

    // Actions
    updateActiveRecipe,
    markExecution,
    updatePageContext,
    clearContext,
  };

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

/**
 * Standalone hook (backwards compatible)
 * Can be used without provider, but with limited functionality
 */
export const useNavigationContextStandalone = () => {
  const location = useLocation();
  const { currUser } = useAppContext();

  // Track active recipe (if on Recipe Designer)
  const [activeRecipe, setActiveRecipe] = useState(null);

  // Track selected tools in current recipe
  const [selectedTools, setSelectedTools] = useState([]);

  // Track recent routes (client-side only)
  const [recentRoutes, setRecentRoutes] = useState([]);

  // Track last execution time
  const [lastExecutionTime, setLastExecutionTime] = useState(null);

  // Update recent routes when location changes
  useEffect(() => {
    const currentRoute = location.pathname.split('/')[1];
    if (currentRoute && !currentRoute.startsWith('recipe-')) {
      setRecentRoutes(prev => {
        const updated = [currentRoute, ...prev.filter(r => r !== currentRoute)];
        return updated.slice(0, 5); // Keep last 5
      });
    }
  }, [location.pathname]);

  // Update tools when recipe changes
  useEffect(() => {
    if (activeRecipe?.steps) {
      const tools = activeRecipe.steps.map(s => s.toolId).filter(Boolean);
      setSelectedTools(tools);
    }
  }, [activeRecipe]);

  // Memoize markExecution callback
  const markExecution = useCallback(() => {
    setLastExecutionTime(Date.now());
  }, []);

  // Memoize current route calculation
  const currentRoute = useMemo(() => {
    return location.pathname.split('/')[1] || '';
  }, [location.pathname]);

  // Memoize the entire return object to prevent unnecessary re-renders
  return useMemo(() => ({
    currentRoute,
    activeRecipe,
    setActiveRecipe,
    selectedTools,
    setSelectedTools,
    recentRoutes,
    userRole: currUser?.role,
    lastExecutionTime,
    markExecution,
  }), [currentRoute, activeRecipe, selectedTools, recentRoutes, currUser?.role, lastExecutionTime, markExecution]);
};

export default useNavigationContext;
